// Constants
import { DueDateCategory } from "@ms/uno-constants/lib/local/DateConstants";
// Models
import { Temporal } from "temporal-polyfill-lite";
// Utilities
import { getDiff, getDiffTemporalCustomizer } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import { getLocaleWeekdayOffset, getSystemTimeZone } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
/**
 * Represents a zoned date with an optional time.
 */ export class DateWithOptionalTime {
    get temporal() {
        if (!this.cachedTemporal) {
            this.cachedTemporal = Temporal.ZonedDateTime.from({
                year: this.date.year,
                month: this.date.month,
                day: this.date.day,
                hour: this.time.hour,
                minute: this.time.minute,
                second: this.time.second,
                timeZone: this.timezone
            });
        }
        return this.cachedTemporal;
    }
    /**
     * Creates a DateWithOptionalTime from date category
     * @param dateCategory The date category to convert
     * @returns DateWithOptionalTime with isTimeSpecified set to false
     */ static fromDateCategory(dateCategory, timeZone = getSystemTimeZone()) {
        const today = Temporal.Now.plainDateISO(timeZone);
        let resultDate;
        // Inline the same locale-aware week-offset arithmetic that
        // getDueDateCategory / isInCurrentWeek / isInNextWeek use, so the date
        // produced here lands in the correct group.
        // dayOfWeek: 1=Mon … 7=Sun; offset: 0=Mon-start, -1=Sun-start, -2=Sat-start
        const daysSinceWeekStart = (today.dayOfWeek - 1 - getLocaleWeekdayOffset() + 7) % 7;
        switch(dateCategory){
            case DueDateCategory.Late:
                // Yesterday
                resultDate = today.subtract({
                    days: 1
                });
                break;
            case DueDateCategory.Today:
                // Today (no change)
                resultDate = today;
                break;
            case DueDateCategory.Tomorrow:
                // Tomorrow
                resultDate = today.add({
                    days: 1
                });
                break;
            case DueDateCategory.ThisWeek:
                // Last day of locale-aware current week
                resultDate = today.add({
                    days: 6 - daysSinceWeekStart
                });
                break;
            case DueDateCategory.NextWeek:
                // Last day of locale-aware next week
                resultDate = today.add({
                    days: 13 - daysSinceWeekStart
                });
                break;
            case DueDateCategory.Future:
                // First day after locale-aware next week
                resultDate = today.add({
                    days: 14 - daysSinceWeekStart
                });
                break;
            case DueDateCategory.NoDate:
            default:
                return null;
        }
        return DateWithOptionalTime.fromPlainDate(resultDate);
    }
    /**
     * Creates a DateWithOptionalTime from a JavaScript Date
     * @param date The JavaScript Date to convert
     * @returns DateWithOptionalTime with isTimeSpecified set to false
     */ static fromDate(date) {
        const day = new Temporal.PlainDateTime(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds());
        return DateWithOptionalTime.fromPlainDate(day.toPlainDate());
    }
    /**
     * Creates a DateWithOptionalTime with default values
     * @param date The date component
     * @returns DateWithOptionalTime with isTimeSpecified set to false
     */ static fromPlainDate(date) {
        return new DateWithOptionalTime(date, DateWithOptionalTime.DEFAULT_TIME, DateWithOptionalTime.DEFAULT_TIMEZONE, false);
    }
    /**
     * Constructs a `DateWithOptionalTime` from a wire-format `IDateWithOptionalTimeResource`.
     * Time parsing is guarded — when `isTimeSpecified` is false (or the wire `time` is missing/empty),
     * the constructor's default time is used instead. The caller should wrap this in try/catch when
     * additional context (entity name, field, id) is needed in the error message.
     */ static fromResource(resource) {
        const plainTime = resource.isTimeSpecified && resource.time ? Temporal.PlainTime.from(resource.time) : DateWithOptionalTime.DEFAULT_TIME;
        return new DateWithOptionalTime(Temporal.PlainDate.from(resource.date), plainTime, resource.timezone, resource.isTimeSpecified);
    }
    /**
     * Constructs a `DateWithOptionalTime` from a UTC ISO instant string (e.g. `"2026-07-06T17:00:00Z"`)
     * Throws if `value` is not a valid ISO instant
     */ static fromUtcInstantString(value) {
        const zoned = Temporal.Instant.from(value).toZonedDateTimeISO("UTC");
        const plainTime = Temporal.PlainTime.from({
            hour: zoned.hour,
            minute: zoned.minute,
            second: zoned.second
        });
        // [TODO:3127713] Hardcoded isTimeSpecified=true — This will change later for "DateOnly" tasks
        return new DateWithOptionalTime(zoned.toPlainDate(), plainTime, DateWithOptionalTime.DEFAULT_TIMEZONE, true);
    }
    applyDiffs(...diff) {
        if (diff.length === 0) {
            return this.getClone();
        }
        let date = this.date;
        let time = this.time;
        let timezone = this.temporal.timeZoneId;
        let isTimeSpecified = this.isTimeSpecified;
        // Loop through each diff and apply changes
        for (const change of diff){
            if (change.date) {
                date = Temporal.PlainDate.from(change.date);
            }
            if (change.time) {
                time = Temporal.PlainTime.from(change.time);
            }
            if (change.timezone) {
                timezone = change.timezone;
            }
            if (change.isTimeSpecified !== undefined) {
                isTimeSpecified = change.isTimeSpecified;
            }
        }
        return new DateWithOptionalTime(date, time, timezone, isTimeSpecified);
    }
    getDiff(target) {
        const diff = getDiff(this, target, getDiffTemporalCustomizer);
        delete diff.temporal; // Exclude temporal from diff as it's derived
        return diff;
    }
    getClone() {
        return new DateWithOptionalTime(Temporal.PlainDate.from(this.date), Temporal.PlainTime.from(this.time), this.timezone, this.isTimeSpecified);
    }
    setProperty(key, value) {
        // Only allow setting of mutable properties
        if (!DateWithOptionalTime.MutableProperties.includes(key)) {
            throw new Error(`Cannot set read-only property. [Model=${this.constructor.name}][Key=${key}]`);
        }
        const clone = this.getClone();
        clone[key] = value; // Using 'any' to bypass type checking for dynamic property setting
        // Create a new instance with the updated property to recalculate temporal
        return new DateWithOptionalTime(clone.date, clone.time, clone.timezone, clone.isTimeSpecified);
    }
    forUpdate() {
        return {
            date: this.date.toString()
        };
    }
    forCreate() {
        return this.forUpdate();
    }
    forSerialization() {
        return {
            timezone: this.timezone,
            isTimeSpecified: this.isTimeSpecified,
            date: this.date.toString(),
            time: this.time?.toString()
        };
    }
    /**
     * Formats the date and time for display purposes (short date format)
     * @param timeZone Optional timezone to use for formatting. If not provided, uses the instance's timezone
     * @returns Formatted date string in short format (MM/DD/YYYY or DD/MM/YYYY based on locale)
     */ toDisplayDateString(timeZone) {
        const tz = timeZone || this.timezone;
        const formattedDate = Temporal.ZonedDateTime.from({
            year: this.date.year,
            month: this.date.month,
            day: this.date.day,
            hour: this.time.hour,
            minute: this.time.minute,
            second: this.time.second,
            timeZone: tz
        });
        return formattedDate.toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        });
    }
    /**
     * Formats the date and time for display purposes (dd/mm/yyyy hh:mm:ss PST(short timezone))
     * @param timeZone Optional timezone to use for formatting. If not provided, uses the instance's timezone
     * @returns Formatted date and time string in full locale format
     */ toDisplayDateTimeString(timeZone) {
        const tz = timeZone || this.timezone;
        const formattedDate = Temporal.ZonedDateTime.from({
            year: this.date.year,
            month: this.date.month,
            day: this.date.day,
            hour: this.time.hour,
            minute: this.time.minute,
            second: this.time.second,
            timeZone: tz
        });
        return formattedDate.toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            timeZoneName: "short"
        });
    }
    /**
     * Formats the date and time using custom formatting options
     * @param locale The locale to use for formatting (e.g., 'en-US', 'fr-FR')
     * @param formatOptions Options for formatting the date/time
     * @returns Formatted string based on the provided options
     */ getFormattedOutput(locale, formatOptions) {
        return this.temporal.toLocaleString(locale, formatOptions);
    }
    toTemporalInstant() {
        return this.temporal.toInstant();
    }
    toUtcInstantString() {
        return this.toTemporalInstant().toString();
    }
    /**
     * Get the PlainDate in the specified timezone.
     * Result is cached per timezone - subsequent calls with the same
     * timezone return the cached value.
     * @param timeZone The timezone to convert to. Defaults to system timezone.
     */ getLocalDate(timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
        // If the requested timezone matches the stored timezone,
        // we can use the date directly without conversion
        if (timeZone === this.timezone) {
            return this.date;
        }
        // Return cached value if timezone matches
        if (this.cachedLocalDate && this.cachedForTimeZone === timeZone) {
            return this.cachedLocalDate;
        }
        // Convert to the target timezone and extract PlainDate
        this.cachedLocalDate = this.temporal.withTimeZone(timeZone).toPlainDate();
        this.cachedForTimeZone = timeZone;
        return this.cachedLocalDate;
    }
    constructor(date, time, timezone, isTimeSpecified){
        this.date = date;
        this.time = time;
        this.timezone = timezone;
        this.isTimeSpecified = isTimeSpecified;
        // Cache for computed temporal value - defined as non-enumerable in constructor
        this.cachedTemporal = null;
        // Cache for getLocalDate - stores the computed PlainDate and the timezone it was computed for
        // These are defined as non-enumerable in the constructor so they don't appear in snapshots
        this.cachedLocalDate = null;
        this.cachedForTimeZone = null;
        this.isTimeSpecified = isTimeSpecified;
        this.date = date;
        if (isTimeSpecified) {
            this.time = time;
            this.timezone = timezone;
        } else {
            // TODO#2800370 - Remove client side override of time/timezone
            this.time = DateWithOptionalTime.DEFAULT_TIME;
            this.timezone = DateWithOptionalTime.DEFAULT_TIMEZONE;
        }
        // Initialize cache fields as non-enumerable so they don't appear in snapshots/JSON
        Object.defineProperty(this, "cachedTemporal", {
            value: null,
            writable: true,
            enumerable: false
        });
        Object.defineProperty(this, "cachedLocalDate", {
            value: null,
            writable: true,
            enumerable: false
        });
        Object.defineProperty(this, "cachedForTimeZone", {
            value: null,
            writable: true,
            enumerable: false
        });
    }
}
DateWithOptionalTime.MutableProperties = [
    "date",
    "time",
    "timezone"
];
// 10:00 AM UTC was chosen as the default time to avoid timezone-related date shifts (except for American Samoa)
DateWithOptionalTime.DEFAULT_TIME = Temporal.PlainTime.from({
    hour: 10,
    minute: 0,
    second: 0
});
DateWithOptionalTime.DEFAULT_TIMEZONE = "Etc/UTC";
